/**
 * Dataset selector.
 *
 * Production and synthetic data live in the same tables and are kept apart by a
 * `dataset_kind` on every row. This is where that choice is made, and it is the
 * only place in the console it can be made — everything else follows the active
 * dataset without asking.
 *
 * Both datasets are always offered, empty or not. A selector that hid the empty
 * one could never be used to start the first synthetic import.
 */

import { useEffect, useRef, useState } from "react";

import { ChevronDown, Database, FlaskConical } from "lucide-react";

import {
  DATASET_KINDS,
  DATASET_LABELS,
  type DatasetKind,
} from "../data/dataset";
import type { DatasetAvailability } from "../data/types";
import { useActiveDataset } from "../hooks/useActiveDataset";

export type DatasetPillProps = {
  /** Row counts per dataset. Kept for callers; the menu no longer displays them. */
  availability?: DatasetAvailability[];
  /** Called after a change, so the page can refetch what it is showing. */
  onChange?: (dataset: DatasetKind) => void;
};

export function DatasetPill({ onChange }: DatasetPillProps) {
  const [dataset, select] = useActiveDataset();
  const [open, setOpen] = useState(false);
  /** Keep the menu mounted through the close transition. */
  const [menuMounted, setMenuMounted] = useState(false);
  const [menuVisible, setMenuVisible] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (open) {
      setMenuMounted(true);
      const frame = requestAnimationFrame(() => {
        requestAnimationFrame(() => setMenuVisible(true));
      });
      return () => cancelAnimationFrame(frame);
    }
    setMenuVisible(false);
    const timer = window.setTimeout(() => setMenuMounted(false), 200);
    return () => window.clearTimeout(timer);
  }, [open]);

  // Close on outside click and on Escape. Without the latter the menu can only
  // be dismissed with a mouse, which is a keyboard trap.
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node))
        setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const isSynthetic = dataset === "synthetic";

  const choose = (next: DatasetKind) => {
    setOpen(false);
    if (next === dataset) return;
    select(next);
    onChange?.(next);
  };

  return (
    <div className="relative inline-block w-fit" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={`Dataset: ${DATASET_LABELS[dataset]}. Change dataset.`}
        className={[
          "inline-flex h-9 w-full items-center gap-2 rounded-xl px-3.5",
          "border border-gray-200 bg-white text-gray-700",
          "text-sm font-medium transition-colors duration-200",
          "hover:border-gray-300 hover:bg-gray-50",
          "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#974ae4]/40",
        ].join(" ")}
      >
        {isSynthetic ? (
          <FlaskConical className="h-3.5 w-3.5 text-gray-500" />
        ) : (
          <Database className="h-3.5 w-3.5 text-gray-500" />
        )}
        <span>
          Dataset:{" "}
          <span className="font-semibold text-gray-900">
            {DATASET_LABELS[dataset]}
          </span>
        </span>
        <ChevronDown
          className={`h-3.5 w-3.5 opacity-70 transition-transform duration-200 ease-out ${
            open ? "rotate-180" : "rotate-0"
          }`}
        />
      </button>

      {menuMounted && (
        <div
          role="listbox"
          className={[
            "absolute left-0 right-0 z-50 mt-2 w-full origin-top overflow-hidden",
            "rounded-xl border border-gray-200 bg-white py-1 shadow-lg",
            "transition-[opacity,transform] duration-200 ease-out",
            "motion-reduce:transition-none",
            menuVisible
              ? "translate-y-0 scale-100 opacity-100"
              : "-translate-y-1.5 scale-[0.96] opacity-0",
          ].join(" ")}
        >
          <ul className="m-0 list-none p-1">
            {DATASET_KINDS.map(kind => {
              const isActive = kind === dataset;
              return (
                <li key={kind}>
                  <button
                    type="button"
                    role="option"
                    aria-selected={isActive}
                    onClick={() => choose(kind)}
                    className={[
                      "flex w-full items-center gap-2.5 rounded-lg px-2.5 py-2 text-left text-sm font-medium",
                      "transition-colors duration-150",
                      isActive
                        ? "bg-violet-50 text-[#974ae4]"
                        : "text-gray-800 hover:bg-gray-50",
                    ].join(" ")}
                  >
                    {kind === "synthetic" ? (
                      <FlaskConical
                        className={`h-4 w-4 shrink-0 ${
                          isActive ? "text-[#974ae4]" : "text-gray-500"
                        }`}
                      />
                    ) : (
                      <Database
                        className={`h-4 w-4 shrink-0 ${
                          isActive ? "text-[#974ae4]" : "text-gray-500"
                        }`}
                      />
                    )}
                    {DATASET_LABELS[kind]}
                  </button>
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </div>
  );
}

/**
 * Inline marker for a synthetic artifact.
 *
 * Renders nothing for production: a badge on every row would be noise, and the
 * thing worth flagging is the exception.
 */
export function SyntheticBadge({
  dataset,
  className = "",
}: {
  dataset: DatasetKind | null | undefined;
  className?: string;
}) {
  if (dataset !== "synthetic") return null;
  return (
    <span
      className={[
        "inline-flex items-center gap-1 rounded-full bg-[rgb(29,78,216)]/10 px-2 py-0.5",
        "text-[11px] font-medium text-[rgb(29,78,216)]",
        className,
      ].join(" ")}
    >
      <FlaskConical className="h-3 w-3 text-[rgb(29,78,216)]" />
      Synthetic
    </span>
  );
}

/**
 * Banner for a whole view whose contents are synthetic.
 *
 * Sits above a tab's data so the reading is framed before it is read, not after.
 */
export function SyntheticNotice({ dataset }: { dataset: DatasetKind }) {
  if (dataset !== "synthetic") return null;
  return (
    <div className="flex items-start gap-2 rounded-lg border border-[rgb(29,78,216)]/25 bg-[rgb(29,78,216)]/10 px-3 py-2">
      <FlaskConical className="mt-0.5 h-4 w-4 shrink-0 text-[rgb(29,78,216)]" />
      <p className="m-0 text-xs text-[rgb(29,78,216)]">
        You are viewing <strong>synthetic</strong> data. These figures are not
        real customer KPIs and must not be used for reporting.
      </p>
    </div>
  );
}
